import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectsStore, Project } from '../store/projects';
import { PageHeader } from '../components/PageHeader';

type ViewMode = 'cards' | 'list';
type FilterType = 'all' | 'own' | 'client';

function CreateProjectModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; owner_type: 'own' | 'client' }) => Promise<void>;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const [name, setName] = useState('');
  const [ownerType, setOwnerType] = useState<'own' | 'client'>('own');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onCreate({ name, owner_type: ownerType });
      setName('');
      setOwnerType('own');
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">{t('projects:newProject')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects:projectName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects:projectType')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="ownerType"
                  value="own"
                  checked={ownerType === 'own'}
                  onChange={() => setOwnerType('own')}
                  className="mr-2"
                />
                {t('projects:ownProject')}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="ownerType"
                  value="client"
                  checked={ownerType === 'client'}
                  onChange={() => setOwnerType('client')}
                  className="mr-2"
                />
                {t('projects:clientProject')}
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('projects:creating') : t('common:create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProjectModal({
  project,
  isOpen,
  onClose,
  onSave,
}: {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, data: { name: string; owner_type: 'own' | 'client' }) => Promise<void>;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const [name, setName] = useState('');
  const [ownerType, setOwnerType] = useState<'own' | 'client'>('own');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setOwnerType(project.owner_type);
    }
  }, [project]);

  if (!isOpen || !project) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(project.id, { name, owner_type: ownerType });
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{t('projects:editProject')}</h2>
          <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
            {project.display_id}
          </span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects:projectName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects:slug')}
            </label>
            <div className="flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-600">
              /{project.slug}
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('projects:slugHint')}</p>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects:projectType')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="editOwnerType"
                  value="own"
                  checked={ownerType === 'own'}
                  onChange={() => setOwnerType('own')}
                  className="mr-2"
                />
                {t('projects:ownProject')}
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="editOwnerType"
                  value="client"
                  checked={ownerType === 'client'}
                  onChange={() => setOwnerType('client')}
                  className="mr-2"
                />
                {t('projects:clientProject')}
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('projects:saving') : t('common:save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  onSelect,
  onEdit,
  onDelete,
}: {
  project: Project;
  onSelect: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div
      className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow cursor-pointer relative"
      onClick={() => onSelect(project)}
    >
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${
              project.owner_type === 'own'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-purple-100 text-purple-800'
            }`}
          >
            {project.owner_type === 'own' ? t('projects:own') : t('projects:client')}
          </span>
          {project.role === 'owner' && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActions(!showActions);
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
                title={t('projects:actions')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {showActions && (
                <div
                  className="absolute right-0 top-8 bg-white border rounded-lg shadow-lg py-1 z-10 min-w-[140px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      onEdit(project);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {t('common:edit')}
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(true);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {t('common:delete')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{project.display_id}</span>
        <span className="text-sm text-gray-500">/{project.slug}</span>
      </div>
      <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
        <span className="capitalize">{project.role}</span>
        <span>{new Date(project.created_at).toLocaleDateString('de-DE')}</span>
      </div>

      {/* Schnellzugriff-Links */}
      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <Link
          to={`/projects/${project.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
          title={t('projects:detail.posts')}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {t('projects:detail.posts')}
        </Link>
        <Link
          to={`/media?project=${project.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
          title={t('projects:detail.media')}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {t('projects:detail.media')}
        </Link>
        <Link
          to={`/creator?project=${project.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
          title={t('projects:detail.creator')}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          {t('projects:detail.creator')}
        </Link>
      </div>

      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex flex-col items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-gray-700 mb-4">{t('projects:confirmDelete')}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={() => onDelete(project.id)}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              {t('common:delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Liste-Zeile Komponente
function ProjectListRow({
  project,
  onSelect,
  onEdit,
  onDelete,
}: {
  project: Project;
  onSelect: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onSelect(project)}
    >
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{project.name}</div>
        <div className="text-xs text-gray-500">/{project.slug}</div>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
          {project.display_id}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex px-2 py-1 text-xs rounded-full ${
            project.owner_type === 'own'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-purple-100 text-purple-800'
          }`}
        >
          {project.owner_type === 'own' ? t('projects:own') : t('projects:client')}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 capitalize">
        {project.role}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {new Date(project.created_at).toLocaleDateString('de-DE')}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          <Link
            to={`/media?project=${project.id}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
            title={t('projects:detail.media')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </Link>
          <Link
            to={`/creator?project=${project.id}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
            title={t('projects:detail.creator')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </Link>
        </div>
      </td>
      <td className="px-4 py-3 text-right relative">
        {project.role === 'owner' && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            {showActions && (
              <div
                className="absolute right-4 top-10 bg-white border rounded-lg shadow-lg py-1 z-10 min-w-[140px]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    onEdit(project);
                    setShowActions(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {t('common:edit')}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(true);
                    setShowActions(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  {t('common:delete')}
                </button>
              </div>
            )}
            {showDeleteConfirm && (
              <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(false);
                }}
              >
                <div
                  className="bg-white rounded-lg p-6 max-w-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="text-gray-700 mb-4">{t('projects:confirmDeleteNamed', { name: project.name })}</p>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      onClick={() => {
                        onDelete(project.id);
                        setShowDeleteConfirm(false);
                      }}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      {t('common:delete')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

export function ProjectsPage() {
  const { t } = useTranslation(['projects', 'common']);
  const { projects, isLoading, error, fetchProjects, createProject, updateProject, deleteProject, setCurrentProject, setSelectedProjectId } =
    useProjectsStore();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Gefilterte und durchsuchte Projekte
  const filteredProjects = useMemo(() => {
    let result = projects;

    // Filter nach Typ
    if (filterType !== 'all') {
      result = result.filter((p) => p.owner_type === filterType);
    }

    // Suche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((p) =>
        p.name.toLowerCase().includes(query) ||
        p.slug.toLowerCase().includes(query) ||
        p.display_id.toLowerCase().includes(query) ||
        p.role.toLowerCase().includes(query)
      );
    }

    return result;
  }, [projects, filterType, searchQuery]);

  const handleCreate = async (data: { name: string; owner_type: 'own' | 'client' }) => {
    await createProject(data);
  };

  const handleEdit = async (id: string, data: { name: string; owner_type: 'own' | 'client' }) => {
    await updateProject(id, data);
  };

  const handleSelect = (project: Project) => {
    setCurrentProject(project);
    setSelectedProjectId(project.id);
    navigate(`/projects/${project.id}`);
  };

  const handleDelete = async (id: string) => {
    await deleteProject(id);
  };

  const ownProjects = filteredProjects.filter((p) => p.owner_type === 'own');
  const clientProjects = filteredProjects.filter((p) => p.owner_type === 'client');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        }
        title={t('projects:title')}
        subtitle={t('projects:subtitle')}
        breadcrumbs={[{ label: t('common:nav.dashboard'), href: '/' }]}
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('projects:newProject')}
          </button>
        }
      />

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Suchfeld */}
          <div className="flex-1 relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={t('projects:search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter und Ansicht */}
          <div className="flex gap-2">
            {/* Typ-Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as FilterType)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('projects:filter.allTypes')}</option>
              <option value="own">{t('projects:filter.own')}</option>
              <option value="client">{t('projects:filter.client')}</option>
            </select>

            {/* Ansicht-Toggle */}
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 ${
                  viewMode === 'cards'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title={t('projects:view.cards')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title={t('projects:view.list')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Ergebnis-Info */}
        {(searchQuery || filterType !== 'all') && (
          <div className="mt-3 text-sm text-gray-500">
            {t('projects:search.resultsCount', { count: filteredProjects.length })}
            {searchQuery && <span> {t('projects:search.forQuery', { query: searchQuery })}</span>}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">{t('common:loading')}</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">{t('projects:empty.title')}</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="text-blue-600 hover:text-blue-800"
          >
            {t('projects:empty.action')}
          </button>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-2">{t('projects:search.noResults')}</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setFilterType('all');
            }}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            {t('projects:search.resetFilters')}
          </button>
        </div>
      ) : viewMode === 'list' ? (
        /* Listenansicht */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('projects:table.project')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('projects:table.id')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('projects:table.type')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('projects:table.role')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('projects:table.created')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('projects:table.quickAccess')}
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <ProjectListRow
                  key={project.id}
                  project={project}
                  onSelect={handleSelect}
                  onEdit={(p) => setEditingProject(p)}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : filterType === 'all' ? (
        /* Kartenansicht mit Gruppierung */
        <>
          {ownProjects.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                {t('projects:sections.ownProjects')}
                <span className="ml-2 text-sm font-normal text-gray-500">({ownProjects.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ownProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={handleSelect}
                    onEdit={(p) => setEditingProject(p)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </section>
          )}

          {clientProjects.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                {t('projects:sections.clientProjects')}
                <span className="ml-2 text-sm font-normal text-gray-500">({clientProjects.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clientProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={handleSelect}
                    onEdit={(p) => setEditingProject(p)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      ) : (
        /* Kartenansicht ohne Gruppierung (gefiltert) */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onSelect={handleSelect}
              onEdit={(p) => setEditingProject(p)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <CreateProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreate}
      />

      <EditProjectModal
        project={editingProject}
        isOpen={!!editingProject}
        onClose={() => setEditingProject(null)}
        onSave={handleEdit}
      />
    </div>
  );
}
